/**
 * ABPsaSigningPage — public seller-signing page for the A-B PSA, reached
 * via the magic-link token emailed from the admin Initiate flow. No auth
 * required (the token IS the authorization). Mirrors pages/CountersignPSA
 * for visual consistency but adds:
 *   - Signature canvas + Initials canvas (sellers initial each page on TREC)
 *   - Typed-name + consent line for E-SIGN compliance
 *   - Stale-copy detection via X-Doorly-Document-Hash header
 *
 * States: loading → review → sign → done → error
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ThirdPartyLogo from '../components/ThirdPartyLogo';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const CONSENT_TEXT =
  'I agree to use electronic records and signatures for this transaction. ' +
  'My typed name and drawn signature below constitute my legal signature ' +
  'under the E-SIGN Act and applicable state law.';

// Canvas-wiring hook — uses a callback ref so the setup runs EXACTLY
// once per canvas mount, not on every parent re-render. The earlier
// useEffect-driven version got re-run every time a sibling state
// (hasStrokes on the other canvas, the consent checkbox, etc.)
// flipped — each re-run reassigned canvas.width, which per HTML5
// spec clears the bitmap. Net effect: drawing the signature wiped
// the initials, ticking the consent box wiped both.
//
// Callback ref pattern: React invokes `refCallback(node)` once when
// the element mounts and `refCallback(null)` once on unmount. With
// useCallback([]) the callback identity is stable, so React doesn't
// re-fire it on re-renders.
function useSignatureCanvas() {
  const [hasStrokes, setHasStrokes] = useState(false);
  const canvasRef = useRef(null);
  const cleanupRef = useRef(null);

  const refCallback = useCallback((canvas) => {
    // Teardown previous wiring if the canvas got swapped out.
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    canvasRef.current = canvas;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width  = Math.round(rect.width  * dpr);
    canvas.height = Math.round(rect.height * dpr);
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0];
      return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move  = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasStrokes(true); };
    const end   = () => { drawing = false; };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove',  move,  { passive: false });
    canvas.addEventListener('touchend',   end);
    cleanupRef.current = () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // DON'T reassign canvas.width — that would clear AND wipe the
    // dpr scale + stroke settings. clearRect on the full backing
    // dimensions does the job; restore the configured stroke style
    // after the setTransform reset.
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasStrokes(false);
  }, []);

  const toDataURL = useCallback((type = 'image/png', quality) => {
    const canvas = canvasRef.current;
    return canvas ? canvas.toDataURL(type, quality) : null;
  }, []);

  return { ref: refCallback, hasStrokes, clear, toDataURL };
}

export default function ABPsaSigningPage() {
  // Capture the signing token ONCE, then strip it from the address bar (effect
  // below) so it never lingers in history / Referer. Refresh-after-strip has no
  // token → a "reopen the link" message, not a crash.
  const [token] = useState(() => new URLSearchParams(window.location.search).get('token'));
  const [state, setState] = useState('loading');
  const [info, setInfo] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [docHash, setDocHash] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [typedName, setTypedName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Stash the captured PNG data URLs so the final commit can resend
  // them without forcing the seller to re-draw. The canvases retain
  // their pixels via the ref, but re-encoding twice is wasteful and
  // could subtly differ if the canvas got re-mounted.
  const [capturedSig, setCapturedSig] = useState(null);
  const [capturedInitials, setCapturedInitials] = useState(null);

  const sig = useSignatureCanvas();
  const initials = useSignatureCanvas();

  // ── Load signing-info + base PDF on mount ───────────────────────────
  // The new layout shows the PDF inline alongside the signature form,
  // so we fetch the document eagerly instead of lazy-on-click.
  // Strip the token from the address bar on mount (history + Referer hygiene).
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!token) { setError('Please reopen the link from your email.'); setState('error'); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/ab-psa/signing-info`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) {
          if (!cancelled) {
            setError(d.error || 'This link is invalid or has expired.');
            setState('error');
          }
          return;
        }
        if (cancelled) return;
        setInfo(d);
        setTypedName(d.signer?.name || '');

        const pdfRes = await fetch(`${API}/api/ab-psa/document`, { headers: { 'X-Signer-Token': token } });
        if (!pdfRes.ok) {
          const ed = await pdfRes.json().catch(() => ({}));
          if (!cancelled) {
            setError(ed.error || 'Unable to load the contract.');
            setState('error');
          }
          return;
        }
        const blob = await pdfRes.blob();
        if (cancelled) return;
        setPdfUrl(URL.createObjectURL(blob));
        setDocHash(pdfRes.headers.get('X-Doorly-Document-Hash'));
        setState('sign');
      } catch (_) {
        if (!cancelled) {
          setError('This link is invalid or has expired.');
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // ── Cleanup object URLs on unmount ───────────────────────────────────
  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Executive counter-sign (Jesse, role 'doorly') — single-stage. Skips the
  // seller preview (that stamps a seller slot); posts the captured signature
  // straight to the token-authed counter-sign endpoint, which finalizes the
  // PSA (status='completed', effective date = now, stage advance + fan-out).
  const handleCountersign = async () => {
    if (!sig.hasStrokes || !initials.hasStrokes) return;
    if (!typedName.trim()) { setError('Please type your full name.'); return; }
    if (!agreed) { setError('Please check the consent box.'); return; }
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/ab-psa/jesse-countersign-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signatureDataUrl: sig.toDataURL('image/png'),
          initialsDataUrl: initials.toDataURL('image/png'),
          typedName: typedName.trim(),
          consentText: CONSENT_TEXT,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setError(d.error || 'Counter-sign failed. Please try again.'); setState('error'); return; }
      setState('done');
    } catch (_) {
      setError('Something went wrong. Please try again.');
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 1 submit — POST to /preview-sign. Server stamps the contract
  // (signature + initials + footer) WITHOUT persisting. We get the
  // stamped PDF back as bytes; seller reviews and confirms in stage 2.
  const handlePreview = async () => {
    // Jesse's executive token has no seller preview stage — finalize directly.
    if (info?.signer?.role === 'doorly') return handleCountersign();
    if (!sig.hasStrokes || !initials.hasStrokes) return;
    if (!typedName.trim()) { setError('Please type your full name.'); return; }
    if (!agreed) { setError('Please check the consent box.'); return; }
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const signatureDataUrl = sig.toDataURL('image/png');
      const initialsDataUrl  = initials.toDataURL('image/png');
      const r = await fetch(`${API}/api/ab-psa/preview-sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signatureDataUrl,
          initialsDataUrl,
          typedName: typedName.trim(),
          consentText: CONSENT_TEXT,
          clientHashBefore: docHash,
        }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setError(d.error || 'Preview failed. Please try again.');
        if (r.status === 409) setState('sign'); // hash mismatch — stay on sign
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      // Replace any prior preview URL (re-edit cycle).
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(url);
      setCapturedSig(signatureDataUrl);
      setCapturedInitials(initialsDataUrl);
      setState('confirm');
    } catch (_) {
      setError('Something went wrong while building the preview. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 2 submit — POST to /sign with the same payload as the preview.
  // Server re-stamps and persists (DELETE+INSERT psa_ab_signed, mark
  // token used, append audit json, advance status when all sellers done).
  const handleFinalSubmit = async () => {
    if (!capturedSig || !capturedInitials) {
      setError('Lost your signature — please go back and re-draw it.');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const r = await fetch(`${API}/api/ab-psa/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          signatureDataUrl: capturedSig,
          initialsDataUrl: capturedInitials,
          typedName: typedName.trim(),
          consentText: CONSENT_TEXT,
          clientHashBefore: docHash,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(d.error || 'Submission failed. Please try again.');
        if (r.status === 409) setState('sign');
        else setState('error');
        return;
      }
      setState('done');
    } catch (_) {
      setError('Something went wrong. Please try again.');
      setState('error');
    } finally {
      setSubmitting(false);
    }
  };

  // Stage 2 "Go Back & Edit" — drop the preview, return to sign step.
  // Existing canvas strokes stay intact via the ref so the seller can
  // tweak rather than start over. Hash is unchanged; final submit will
  // still match the source PDF.
  const handleEditAgain = () => {
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
    setError(null);
    setState('sign');
  };

  // ── Render states ────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', color: '#9ca3af' }}>
        <div style={{ width: 28, height: 28, border: '3px solid #e5e7eb', borderTopColor: '#3a6ad9', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <div style={{ color: '#ff082f', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>This link can't be used right now.</div>
          <div style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>{error || 'Contact support@godoorly.com for assistance.'}</div>
        </div>
      </div>
    );
  }

  if (state === 'done') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20 }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>✓</div>
          <div style={{ color: '#166534', fontSize: 20, fontWeight: 800, marginBottom: 10 }}>
            {info?.signer?.role === 'doorly' ? 'Counter-signed — the PSA is fully executed.' : 'Thanks — your signature is recorded.'}
          </div>
          <div style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
            {info?.signer?.role === 'doorly'
              ? 'The Purchase and Sale Agreement is now fully executed. The contract has been finalized and the file advanced — a copy is on the borrower\'s Documents page.'
              : 'We\'ll route the contract to the remaining signers and notify you when it\'s fully executed. A copy of the signed PSA will be emailed to you when complete.'}
          </div>
        </div>
      </div>
    );
  }

  // Header subtitle. Sellers see "Seller's Signature"; Jesse's executive
  // counter-sign token ('doorly' role) sees the counter-signature label.
  const isCountersign = info?.signer?.role === 'doorly';
  const headerSub = isCountersign ? 'Executive Counter-signature' : "Seller's Signature";
  const otherSellers = (info?.sellers || []).filter(
    (s) => String(s.email || '').toLowerCase() !== String(info?.signer?.email || '').toLowerCase()
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f9', padding: '40px 20px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.08)' }}>

        <ThirdPartyLogo band />
        <div style={{ background: '#3a6ad9', padding: '14px 36px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Purchase and Sale Agreement · {headerSub}</div>
        </div>

        <div style={{ padding: '28px 36px 8px' }}>
          <p style={{ margin: '0 0 6px', fontSize: 13, color: '#6b7280' }}>Hi {info?.signer?.name || (isCountersign ? 'Jesse' : 'Seller')},</p>
          <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 800, color: '#111827', lineHeight: 1.3 }}>
            {isCountersign
              ? 'The seller-signed PSA is ready for your counter-signature.'
              : "Doorly's offer is ready for your signature."}
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
            {info?.propertyAddress || ''}
          </p>
          {info?.priorSigners?.length > 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#166534' }}>
              ✓ Already signed: {info.priorSigners.map((s) => s.name).join(', ')}
            </p>
          )}
          {otherSellers.length > 0 && info?.priorSigners?.length === 0 && (
            <p style={{ margin: '10px 0 0', fontSize: 12, color: '#9ca3af' }}>
              Other sellers on this contract: {otherSellers.map((s) => s.name || s.email).join(', ')}
            </p>
          )}
        </div>

        {state === 'sign' && (
          <>
            {/* Embedded PDF — scroll through the full contract before
                signing. Open-in-new-tab is still available via the
                native PDF viewer toolbar. */}
            <div style={{ padding: '16px 36px 8px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Step 1 · Review the Contract
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#f9fafb' }}>
                {pdfUrl ? (
                  <iframe title="A-B PSA Contract"
                    src={`${pdfUrl}#toolbar=1&navpanes=0&zoom=85`}
                    style={{ display: 'block', width: '100%', height: '70vh', minHeight: 500, border: 'none' }} />
                ) : (
                  <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Loading contract…
                  </div>
                )}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9ca3af' }}>
                Scroll through the full document above before signing.
              </p>
            </div>

            {/* Signature form — sits below the contract for the
                scroll-then-sign UX. */}
            <div style={{ padding: '24px 36px 28px', borderTop: '1px solid #f3f4f6', marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Step 2 · Sign Below
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Full legal name
                </label>
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your full legal name"
                  style={{ width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #d1d5db', borderRadius: 8, boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Signature
                </label>
                <div style={{ position: 'relative', border: '1px solid #d1d5db', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
                  <canvas ref={sig.ref} style={{ display: 'block', width: '100%', height: 160, touchAction: 'none', cursor: 'crosshair' }} />
                  {!sig.hasStrokes && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#d1d5db', fontStyle: 'italic', fontSize: 13 }}>
                      Sign here
                    </div>
                  )}
                </div>
                <button onClick={sig.clear} style={{ marginTop: 8, padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Clear signature
                </button>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Initials
                </label>
                <div style={{ position: 'relative', border: '1px solid #d1d5db', borderRadius: 10, background: '#fff', overflow: 'hidden', width: 200 }}>
                  <canvas ref={initials.ref} style={{ display: 'block', width: '100%', height: 90, touchAction: 'none', cursor: 'crosshair' }} />
                  {!initials.hasStrokes && (
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#d1d5db', fontStyle: 'italic', fontSize: 12 }}>
                      Your initials
                    </div>
                  )}
                </div>
                <button onClick={initials.clear} style={{ marginTop: 8, padding: '6px 14px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  Clear initials
                </button>
              </div>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
                <span style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{CONSENT_TEXT}</span>
              </label>

              {error && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button onClick={handlePreview}
                  disabled={!sig.hasStrokes || !initials.hasStrokes || !typedName.trim() || !agreed || submitting}
                  style={{
                    marginLeft: 'auto',
                    padding: '12px 28px',
                    background: sig.hasStrokes && initials.hasStrokes && typedName.trim() && agreed && !submitting ? '#3a6ad9' : '#e5e7eb',
                    color:      sig.hasStrokes && initials.hasStrokes && typedName.trim() && agreed && !submitting ? '#fff'    : '#9ca3af',
                    border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14,
                    cursor: sig.hasStrokes && initials.hasStrokes && typedName.trim() && agreed && !submitting ? 'pointer' : 'not-allowed',
                  }}>
                  {isCountersign
                    ? (submitting ? 'Counter-signing…' : 'Counter-sign Contract →')
                    : (submitting ? 'Building preview…' : 'Preview Signed Contract →')}
                </button>
              </div>
            </div>
          </>
        )}

        {state === 'confirm' && (
          <>
            <div style={{ padding: '20px 36px 8px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#166534', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Step 3 · Review & Submit
              </div>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#374151', lineHeight: 1.6 }}>
                Your signature and initials have been applied below. Scroll through the signed contract,
                then submit to record it.
              </p>
              <div style={{ border: '1px solid #86efac', borderRadius: 10, overflow: 'hidden', background: '#f0fdf4' }}>
                {previewPdfUrl ? (
                  <iframe title="A-B PSA Signed Preview"
                    src={`${previewPdfUrl}#toolbar=1&navpanes=0&zoom=85`}
                    style={{ display: 'block', width: '100%', height: '70vh', minHeight: 500, border: 'none' }} />
                ) : (
                  <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    Loading signed preview…
                  </div>
                )}
              </div>
            </div>

            <div style={{ padding: '20px 36px 28px' }}>
              {error && (
                <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fef2f2', color: '#991b1b', borderRadius: 8, fontSize: 13 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={handleEditAgain} disabled={submitting}
                  style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer' }}>
                  ← Go Back & Edit
                </button>
                <button onClick={handleFinalSubmit} disabled={submitting}
                  style={{
                    marginLeft: 'auto',
                    padding: '12px 28px',
                    background: submitting ? '#bfdbfe' : '#16a34a',
                    color: '#fff',
                    border: 'none', borderRadius: 10, fontWeight: 800, fontSize: 14,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                  }}>
                  {submitting ? 'Submitting…' : 'Submit Signed Contract ✓'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
